import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
};

type ErrorBoundaryState = { error: Error | null };

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, info);
    }
  }

  reset = () => this.setState({ error: null });

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return <DefaultBoundaryFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

function DefaultBoundaryFallback({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation('common');
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>{t('boundary.title')}</CardTitle>
          <CardDescription>{t('boundary.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {import.meta.env.DEV ? (
            <pre className="overflow-auto rounded-md bg-muted p-3 text-xs text-muted-foreground">
              {error.message}
            </pre>
          ) : null}
          <div className="flex gap-2">
            <Button onClick={reset}>{t('boundary.tryAgain')}</Button>
            <Button asChild variant="outline">
              <Link to="/shipments">{t('boundary.backToShipments')}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
